// ─── Event Taxonomy ──────────────────────────────────────────────────────────
//
// NAMING CONVENTION
// ─────────────────
// Navigation / CTA:    button-from_{origin}-to_{destination}   (camelCase origin/dest)
// Language:            language_switch
// Service interest:    {action}_{serviceId}                    (service type in name)
// Intake funnel:       form_{step}_{serviceId}
//
// SERVICE INTEREST FUNNEL — answers "어떤 서비스에 제일 흥미를 느끼나"
// ─────────────────────────────────────────────────────────────────────
//  Signal strength  │ Event name            │ What it means
//  ─────────────────┼───────────────────────┼──────────────────────────
//  Weak             │ viewService_check      │ Scrolled to section (saw it)
//  Medium           │ anchorClick_check      │ Actively jumped to section
//  Strong           │ paymentLink_check      │ Clicked payment button
//  Conversion       │ form_submit_check      │ Completed intake form
//
// All service events accept a `from` param to track which surface drove the action:
//   from: "servicePage" | "homeTier" | "homeFinalCta" | "serviceBrief" | "direct"
//
// GA4 ANALYSIS TIPS
// ─────────────────
// • Funnel Exploration: viewService_* → anchorClick_* → paymentLink_* → form_submit_*
// • Compare services: group events ending in _check vs _core vs _signature
// • Source analysis: filter by `from` param to find highest-converting surface
//
// GA4 ↔ Meta Pixel mapping
// ─────────────────────────
// │ Our event prefix/name      │ Meta Pixel          │
// │────────────────────────────│─────────────────────│
// │ paymentLink_*              │ InitiateCheckout    │
// │ form_submit_*              │ Lead                │
// │ purchase                   │ Purchase            │
// │ page_view (auto)           │ PageView            │
// │ others                     │ trackCustom (same)  │
//
// HOW TO ADD A NEW EVENT
// ──────────────────────
// Static  → add constant to EVENTS below
// Dynamic → add helper to EVENT_FN below
// If Meta standard event → add prefix/name to META_PREFIX_MAP in metaPixel.ts

// ─── Static events ───────────────────────────────────────────────────────────

export const EVENTS = {
  // ── Navbar ────────────────────────────────────────────────────────────────
  CLICK_NAVBAR_HOME:    "button-from_navbar-to_home",
  CLICK_NAVBAR_SERVICE: "button-from_navbar-to_service",
  CLICK_NAVBAR_ABOUT:   "button-from_navbar-to_about",
  CLICK_NAVBAR_FAQ:     "button-from_navbar-to_faq",

  // ── Footer ────────────────────────────────────────────────────────────────
  CLICK_FOOTER_HOME:               "button-from_footer-to_home",
  CLICK_FOOTER_LINKEDIN_COMMUNITY: "button-from_footer-to_linkedin_community",
  CLICK_FOOTER_LINKEDIN:           "button-from_footer-to_linkedin",
  CLICK_FOOTER_INSTAGRAM:          "button-from_footer-to_instagram",
  CLICK_FOOTER_EMAIL:              "button-from_footer-to_email",

  // ── Home page ─────────────────────────────────────────────────────────────
  CLICK_HERO_TO_SERVICE:         "button-from_hero-to_service",
  CLICK_HOME_TIER_TO_APPLY:      "button-from_homeTier-to_service",   // params: { tier_name }
  CLICK_HOME_FINAL_TO_DIAGNOSE:  "button-from_homeFinalCta-to_diagnose",
  CLICK_HOME_FINAL_TO_SERVICE:   "button-from_homeFinalCta-to_service",

  // ── Service page (non-service-specific) ───────────────────────────────────
  CLICK_SERVICE_NOT_SURE:       "button-from_servicePage-to_checkService",
  CLICK_SERVICE_BRIEF_TO_APPLY: "button-from_serviceBrief-to_service",
  CLICK_SERVICE_UNAVAILABLE_TO_DIAGNOSE: "button-from_serviceUnavailableModal-to_checkService",

  // ── Other pages ───────────────────────────────────────────────────────────
  CLICK_RESULTS_TO_SERVICE:         "button-from_results-to_service",
  CLICK_NOT_SURE_NAV:               "button-from_notSure-to_page",     // params: { destination }
  CLICK_COHORT0_PROGRAM:            "button-from_cohort0-to_program",
  CLICK_COHORT0_APPLY:              "button-from_cohort0-to_apply",
  CLICK_HOME_TESTIMONIALS_VIEW_ALL: "button-from_homeTestimonials-to_testimonials",
  CLICK_ABOUT_NAV_RESULTS:      "button-from_aboutNav-to_results",
  CLICK_ABOUT_NAV_TESTIMONIALS: "button-from_aboutNav-to_testimonials",
  // Testimonial filter/readMore → see EVENT_FN.testimonialFilter / testimonialReadMore

  // ── Language ─────────────────────────────────────────────────────────────
  LANGUAGE_SWITCH: "language_switch",                                  // params: { lang }

  // ── Conversion ────────────────────────────────────────────────────────────
  // Fired once per session when user lands on /intake (post-payment).
  // params: { service_type, page_path, referrer, session_id }
  PURCHASE: "purchase",

  // ── Interest capture modal (email lead capture, shown once site-wide) ─────
  // params: { from: "landing" | "service" }
  VIEW_INTEREST_MODAL:    "interestModal_view",
  DISMISS_INTEREST_MODAL: "interestModal_dismiss",
  SUBMIT_INTEREST_MODAL:  "interestModal_submit",
} as const;

export type EventName = (typeof EVENTS)[keyof typeof EVENTS] | string;

// ─── Dynamic event name helpers ───────────────────────────────────────────────
//
// These produce names like "paymentLink_check", "viewService_core", etc.
// The service type is encoded in the name so GA4 Events table shows all
// variants as separate rows — no need to click into params to compare.
//
// `from` param values (pass as second argument where relevant):
//   "servicePage"   — main /service page
//   "homeTier"      — homepage service tier cards
//   "homeFinalCta"  — homepage final CTA section
//   "serviceBrief"  — ServiceBrief component
//   "direct"        — direct URL / unknown

export const EVENT_FN = {
  // ── Service interest signals ──────────────────────────────────────────────

  // Scrolled to service section (30% visible). Fires once per page load.
  viewService: (serviceId: string) =>
    `viewService_${serviceId}`,

  // Clicked the quick-nav anchor at top of service page.
  anchorClick: (serviceId: string) =>
    `anchorClick_${serviceId}`,

  // Clicked the payment CTA button. `from` shows which surface drove it.
  paymentLink: (serviceId: string, from: string = "servicePage") =>
    `paymentLink_${serviceId}`,                // name encodes service
  // Note: pass { from } as params so source is queryable separately

  // Shown the "service temporarily unavailable" modal (core/signature paused).
  serviceUnavailable: (serviceId: string) =>
    `serviceUnavailable_${serviceId}`,

  // Submitted the "notify me when it reopens" form inside that modal.
  serviceUnavailableNotify: (serviceId: string) =>
    `serviceUnavailableNotify_${serviceId}`,

  // ── Testimonials ─────────────────────────────────────────────────────────
  // Maps Korean service labels → English IDs for consistent event naming.
  // "진단" → check, "핵심" → core, "시그니처" → signature, "30분 미팅" → meeting30, "전체" → all
  testimonialFilter: (koreanType: string) => {
    const id = { "진단": "diagnosis", "핵심": "core", "시그니처": "signature", "30분 미팅": "meeting-30", "전체": "all" }[koreanType] ?? koreanType;
    return `filter_testimonials_${id}`;
  },
  testimonialReadMore: (koreanType: string | null) => {
    const id = { "진단": "diagnosis", "핵심": "core", "시그니처": "signature", "30분 미팅": "meeting-30" }[koreanType ?? ""] ?? "unknown";
    return `testimonials_readMore_${id}`;
  },

  // ── Intake funnel ─────────────────────────────────────────────────────────
  // Produces: form_start_diagnose, form_step1_diagnose, form_step2_diagnose, form_submit_diagnose
  // GA4 Funnel Exploration: add all four steps, filter by service type.

  intakeFunnel: (step: "start" | "step1" | "step2" | "submit", serviceType: string) =>
    `form_${step}_${serviceType}`,
};
